import { CommonModule } from '@angular/common';
import { AfterViewChecked, AfterViewInit, Component, DestroyRef, HostListener, ViewChild } from '@angular/core';
import { inject, OnInit, signal } from '@angular/core';
import { ClaimsService } from '../../core/services/claims.service';
import { Geography } from '../../shared/models/geography.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GeographySelectorComponent } from '../geography-selector/geography-selector';
import { ExpirySelectorComponent } from '../expiry/expiry';
import { AuthorizationService } from '../../core/services/authorization.service';
import { UserGeography } from '../../shared/models/user-geography.model';
import { ControlBarComponent } from '../../shared/components/control-bar/control-bar.component';
import { AgGridLicenseService } from '../../core/services/ag-grid-license.service';
import { ClaimRequest } from '../../shared/models/claim-request.model';
import { MainGridComponent } from './main-grid/main-grid.component';
import { Title } from '@angular/platform-browser';
import { trackGoogleAnalyticsEvent } from '../../shared/utilities/common.utility';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'emma-claims',
  standalone: true,
  imports: [CommonModule, GeographySelectorComponent, ExpirySelectorComponent, ControlBarComponent, MainGridComponent],
  templateUrl: './claims.component.html',
  styleUrl: './claims.component.scss',
})

export class ClaimsComponent implements OnInit, AfterViewChecked{
  @ViewChild(MainGridComponent) public mainGrid!: MainGridComponent;
  @ViewChild(ControlBarComponent) public controlBar!: ControlBarComponent;
  public projectGeographies = signal<Array<Geography>>([]);
  public isLoadingProjectGeographies = signal<boolean>(false);
  public projectGeographiesError = signal<string | null>(null);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly claimsService = inject(ClaimsService);
  public geographies = signal<Array<UserGeography>>([]);
  public isLoadingGeographies = signal<boolean>(false);
  public geographiesError = signal<string | null>(null);
  private readonly titleService = inject(Title);
  public hidden  = false;
  public readonly route = inject(ActivatedRoute);

  public isLoadButtonEnable = true;
  public projectCode = signal<string>('');
  private readonly agGridLicenseService = inject(AgGridLicenseService);
  public claimRequest = signal<ClaimRequest | null>(null);
  public showMainGrid = signal<boolean>(false);
  private pendingLoadClaims = false;
  public rowCount = 0;

  constructor(
  ) {
    if (this.route.data) {
      this.route.data.subscribe((data) => {
        this.hidden = data['hidden'];
      });
    }
  }
  public ngAfterViewChecked(): void {
    if (this.pendingLoadClaims && this.mainGrid) {
      this.pendingLoadClaims = false;
      this.mainGrid.loadClaims();
    }

    if (this.controlBar && this.mainGrid && !this.controlBar.mainGridRef) {
      this.controlBar.mainGridRef = this.mainGrid;
    }
  }

  public ngOnInit(): void {
    this.agGridLicenseService.updateAgGridLicense();
    const storedProjectCode = localStorage.getItem('projectCode') ?? '';
    if (storedProjectCode && !this.hidden) {
      const title = `Claim-Management - ${localStorage.getItem('projectName') ?? ''}`;
      this.titleService.setTitle(title);
      trackGoogleAnalyticsEvent(title);
      this.projectCode.set(storedProjectCode);
      this.onProjectSelected(storedProjectCode);
    } 
  }

  private onProjectSelected(selectedProjectCode: string): void {
    if (!selectedProjectCode) {
      return;
    }
    this.loadUserGeographies();
    this.loadGeographies(selectedProjectCode);
  }

  public loadGeographies(projectCode: string): void {
    this.isLoadingProjectGeographies.set(true);
    this.projectGeographiesError.set(null);
    this.claimsService.getProjectGeographies(projectCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (geographies: Array<Geography>) => {
          this.projectGeographies.set(geographies);
          this.isLoadingProjectGeographies.set(false);
        },
        error: () => {
          this.projectGeographiesError.set('Failed to load project geographies');
          this.isLoadingProjectGeographies.set(false);
          this.projectGeographies.set([]);
        }
      });
  }

  private loadUserGeographies(): void {
    this.isLoadingGeographies.set(true);
    this.geographiesError.set(null);

    this.authorizationService.getGeographies()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (geographies: Array<UserGeography>) => {
          this.geographies.set(geographies);
          this.isLoadingGeographies.set(false);
        },
        error: () => {
          this.geographiesError.set('Failed to load geographies');
          this.isLoadingGeographies.set(false);
          this.geographies.set([]);
        }
      });
  }
  @HostListener('document:claims.load-grid-data')
  public loadSizesGridData(): void {
    this.showMainGrid.set(true);
    this.pendingLoadClaims = true;
  }

  public onGridSelectionChanged(hasSelection: boolean): void {
    if (this.controlBar) {
      this.controlBar.onSelectionChanged(hasSelection);
    }
  }
}
